import type { CSSProperties, ImgHTMLAttributes } from 'react';
import { useTheme } from '@mui/material/styles';

export type HFLogoVariant = 'globe' | 'poweredBy' | 'wordMark';

export type HFLogoProps = {
  variant?: HFLogoVariant;
  /** Explicit width in CSS pixels. When omitted, derived from `height` + aspect ratio. */
  width?: number;
  /** Explicit height in CSS pixels. When omitted, derived from `width` + aspect ratio. */
  height?: number;
  className?: string;
  style?: CSSProperties;
  alt?: string;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height' | 'alt' | 'style'>;

/** Intrinsic viewBox ratios for public logo assets. */
const ASPECT: Record<HFLogoVariant, number> = {
  globe: 1,
  poweredBy: 688.668 / 169.205,
  wordMark: 982.857 / 165,
};

const DEFAULT_HEIGHT: Record<HFLogoVariant, number> = {
  globe: 32,
  poweredBy: 45,
  wordMark: 28,
};

const SRC: Record<HFLogoVariant, { light: string; dark: string }> = {
  globe: { light: '/logo-globe.svg', dark: '/logo-globe.svg' },
  poweredBy: { light: '/logo-powered.svg', dark: '/logo-powered-dark.svg' },
  wordMark: { light: '/logo-wordmark.svg', dark: '/logo-wordmark-dark.svg' },
};

const ALT: Record<HFLogoVariant, string> = {
  globe: 'HedgeFacts',
  poweredBy: 'Powered by HedgeFacts',
  wordMark: 'HedgeFacts',
};

/**
 * HedgeFacts logo — public SVG assets only (`core/public/`).
 * `poweredBy` / `wordMark` pick light vs dark files from the theme.
 */
export function HFLogo({ variant = 'wordMark', height, width, className, style, alt, ...imgProps }: HFLogoProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const src = isDark ? SRC[variant].dark : SRC[variant].light;
  const ratio = ASPECT[variant];

  let renderedWidth: number;
  let renderedHeight: number;
  if (width != null && height != null) {
    renderedWidth = width;
    renderedHeight = height;
  } else if (width != null) {
    renderedWidth = width;
    renderedHeight = Math.round(width / ratio);
  } else if (height != null) {
    renderedHeight = height;
    renderedWidth = Math.round(height * ratio);
  } else {
    renderedHeight = DEFAULT_HEIGHT[variant];
    renderedWidth = Math.round(renderedHeight * ratio);
  }

  return (
    <img
      src={src}
      alt={alt ?? ALT[variant]}
      width={renderedWidth}
      height={renderedHeight}
      className={className}
      style={{ display: 'block', maxWidth: width ?? undefined, ...style }}
      {...imgProps}
    />
  );
}
